/**
 * The same bars of the source as the arranger took, so the two can be read side by side.
 *
 * The pipeline numbers bars by their position in the part, 1-based, and so does the score
 * document, so bar `n` is index `n - 1` in both and the window needs no translation. Cutting
 * the original down rather than drawing all of it is not only kinder to the engraver: a
 * comparison is only a comparison when both sides hold the same music.
 *
 * A signature is stated once and holds until it is restated, so cutting the front off a piece
 * throws away the bar that stated the key, the metre and the tempo of every bar after it. The
 * window would then be drawn in C major and played at whatever speed a player picks, which is
 * neither what the file says nor what the arranger read. What was in force at the first bar
 * taken is therefore restated on it -- carried, not invented, and only where the cut itself
 * states nothing.
 */

import { applyAll, fromMusicXml, setup, toMusicXml } from "@app/score-editor";
import type { Command, KeySignature, Score, Tempo, TimeSignature } from "@app/score-editor";

/**
 * The most bars the board draws of either panel.
 *
 * The engraver lays out every page before it shows the first, on the page's own thread, at
 * about 14 ms a bar for a 36-staff orchestra and 5 ms for a wind quintet: all 943 bars of
 * Beethoven's Ninth finale held the board frozen for 33 s. So a longer window is drawn this
 * far from its start on both sides, and the arrangement is still written and saved whole.
 */
export const DRAWN_BARS = 64;

/** The bars a panel heading names: `a–b`, or `a–b of a–c` when fewer are drawn than taken. */
function drawnBars(from: number, drawn: number, taken: number): string {
  const shown = `${from}–${from + drawn - 1}`;
  return drawn < taken ? `${shown} of ${from}–${from + taken - 1}` : shown;
}

/** What the timeline states at bar `index`, reading back to the last bar that stated it. */
function inForce<T>(
  score: Score,
  index: number,
  stated: (measure: Score["measures"][number]) => T | null,
): T | null {
  for (let at = Math.min(index, score.measures.length - 1); at >= 0; at -= 1) {
    const measure = score.measures[at];
    if (measure === undefined) continue;
    const value = stated(measure);
    if (value !== null) return value;
  }
  return null;
}

/** Bars `first` to `last` of `score`, or the reason that window could not be cut out. */
export function barsOf(
  score: Score,
  first: number,
  last: number,
): { score: Score | null; refused: string | null } {
  const total = score.measures.length;
  const from = Math.max(1, Math.trunc(first));
  const to = Math.min(total, Math.trunc(last));
  if (to < from) return { score: null, refused: `this score has no bars ${first}–${last}` };
  if (from === 1 && to === total) return { score, refused: null };

  // Sliced directly rather than through the editor's remove-bar command. The command does the
  // same two things -- the bar leaves the timeline, and every part's music in it goes -- but
  // it copies each part's bars once per bar removed, and cutting sixteen bars out of a
  // 943-bar symphony that way took seven seconds on the page. Nothing here is undone, so the
  // inverse the command also builds is not wanted either.
  const kept = score.measures.slice(from - 1, to);
  const going = [...score.measures.slice(0, from - 1), ...score.measures.slice(to)];
  const parts = score.parts.map((part) => {
    const measures = { ...part.measures };
    for (const measure of going) delete measures[measure.id];
    return { ...part, measures };
  });
  const cut: Score = { ...score, measures: kept, parts };

  const head = cut.measures[0];
  if (head === undefined || from === 1) return { score: cut, refused: null };
  const back = from - 1;
  const carried: (Command | string)[] = [];
  if (head.key === null) {
    const key = inForce<KeySignature>(score, back, (measure) => measure.key);
    if (key !== null) carried.push(setup.setKeyAt(cut, head.id, key));
  }
  if (head.time === null) {
    const time = inForce<TimeSignature>(score, back, (measure) => measure.time);
    if (time !== null) carried.push(setup.setTimeAt(cut, head.id, time));
  }
  if (head.tempo === null) {
    const tempo = inForce<Tempo>(score, back, (measure) => measure.tempo);
    if (tempo !== null) carried.push(setup.setTempoAt(cut, head.id, tempo));
  }
  // A refusal here would name a bar that was just read out of this very score, so it cannot
  // happen; leaving that signature unstated is in any case a drawing the window survives.
  const commands = carried.filter((one): one is Command => typeof one !== "string");
  if (commands.length === 0) return { score: cut, refused: null };
  const stated = applyAll(cut, commands, "carry the signatures in force");
  return stated.ok ? { score: stated.score, refused: null } : { score: cut, refused: null };
}

/**
 * A MusicXML document -- the upload or the arrangement -- as a score the editor can draw, or
 * why it could not be read.
 */
export function parsed(musicxml: string | null): { score: Score | null; reason: string | null } {
  if (musicxml === null) return { score: null, reason: null };
  const result = fromMusicXml(musicxml);
  if (!result.ok) return { score: null, reason: result.reason };
  return { score: result.score, reason: null };
}

/** The bars of the upload as they stand, or why they could not be drawn. */
export type Original =
  | {
      readonly score: Score;
      readonly musicxml: string;
      readonly fileName: string;
      readonly bars: string;
    }
  | { readonly reason: string };

/**
 * The bars of the upload named `name`, cut to the window asked for and shown as soon as a
 * file is picked.
 *
 * It does not wait for an arrangement. A piece you have just opened is the one thing on this
 * board you already know the sound of, and seeing it drawn is how you tell that the right
 * file and the right bars went in before anything is asked of the rules. Once the arranger
 * has run, the window narrows to the bars it actually read, so the two panels hold the same
 * music and the comparison is honest.
 */
export function original(
  name: string | null,
  read: ReturnType<typeof parsed>,
  first: number,
  last: number,
): Original | null {
  if (name === null) return null;
  if (read.score === null) return { reason: read.reason ?? "the score could not be read" };
  const window = barsOf(read.score, first, Math.min(last, first + DRAWN_BARS - 1));
  if (window.score === null) return { reason: window.refused ?? "those bars could not be cut out" };
  const from = Math.max(1, first);
  const drawn = window.score.measures.length;
  const to = from + drawn - 1;
  return {
    score: window.score,
    // Written out of the cut rather than handed back as the upload: this panel is headed with
    // a bar window, and a download holding the whole piece would not be that window.
    musicxml: toMusicXml(window.score),
    fileName: `${name.replace(/\.[^.]+$/, "")}-bars-${from}-${to}.musicxml`,
    bars: drawnBars(from, drawn, Math.min(last, read.score.measures.length) - from + 1),
  };
}
